import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Typography,
  TextField,
  Box,
  Tooltip,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { GetDailyWagesAttendanceDataHelper, UpdateDailyWagesAttendanceDataHelpesr } from "./DailyWageTemplateHelper";
import { grey, red } from "@mui/material/colors";
import { toast } from "react-toastify";

const DailyWageEmployeeWorkingHoursDialog = ({
  content,
  open,
  onClose,
  f_year,
  f_month,
  handleGenerate = () => {},
}) => {
  const [employees, setEmployees] = useState([]);
  const [isLoadingbtn, setIsLoadingbtn] = useState(false);
  const allowEdit = false; // edit is not required since they can regularise; Team: Regularization
  useEffect(() => {
    if (!open) return;
    setIsLoadingbtn(true);
    GetDailyWagesAttendanceDataHelper({
        month: f_month,
        year: f_year,
      }).then((res) =>{ 
        setIsLoadingbtn(false);
          if(res?.data?.statuscode === 200) {
            setEmployees(
                (res?.data?.data || [])?.map((item) => ({
                  ...item,
                  editing: false,
                  updating: false,
                  total_hours: secondsToHours(item?.total_seconds),
                })),
              );
          } else {
            toast.error("Something went wrong")
          }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const secondsToHours = (seconds) => {
    return Number((Number(seconds || 0) / 3600).toFixed(2));
  };

  const hoursToSeconds = (hours) => {
    return Math.round(Number(hours || 0) * 3600);
  };

  const handleEdit = (employee_fk) => {
    setEmployees((prev) =>
      prev?.map((employee) =>
        employee?.employee_fk === employee_fk
          ? {
              ...employee,
              editing: true,
            }
          : employee,
      ),
    );
  };

  const handleHoursChange = (employee_fk, event) => {
    const value = event.target.value;

    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }
    setEmployees((prev) =>
      prev?.map((employee) =>
        employee?.employee_fk === employee_fk
          ? {
              ...employee,
              total_hours: value,
            }
          : employee,
      ),
    );
  };

  const handleSaveRow = async (employee_fk) => {
    const employee = employees?.find(
      (item) => item.employee_fk === employee_fk,
    );

    if (!employee) return;

    const total_seconds = hoursToSeconds(employee.total_hours);

    setEmployees((prev) =>
      prev?.map((item) =>
        item.employee_fk === employee_fk
          ? {
              ...item,
              updating: true,
            }
          : item,
      ),
    );

    await UpdateDailyWagesAttendanceDataHelpesr({
      total_seconds,
      employee_fk,
      month: f_month,
      year: f_year,
    }).then((res) => {
      if (res?.data?.statuscode === 200) {
        const findUpdatedEmp = res?.data?.data?.find(
          (el) => el?.employee_fk === employee_fk,
        );
        const totalSec = findUpdatedEmp?.total_seconds;
        setEmployees((prev) =>
          prev?.map((item) =>
            item.employee_fk === employee_fk
              ? {
                  ...item,
                  total_seconds: totalSec,
                  total_hours: secondsToHours(totalSec),
                  editing: false,
                  updating: false,
                }
              : item,
          ),
        );
      } else {
        setEmployees((prev) =>
          prev?.map((item) =>
            item.employee_fk === employee_fk
              ? {
                  ...item,
                  updating: false,
                }
              : item,
          ),
        );
      }
    });
  };
  const hasUnsavedEdits = employees?.some((employee) => employee?.editing);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {content}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            color: "text.secondary",
          }}
        >
         Review the total working hours for employees for this pay period.
        </Typography>
        {isLoadingbtn ? (
          <Box
            sx={{
              minHeight: 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
        <Grid container spacing={1}>
          {employees?.map((employee) => (
            <Grid item xs={12} md={6} key={employee.employee_fk}>
              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {employee.employee_name ||
                      employee.name ||
                      `Employee ${employee.employee_fk}`}
                  </Typography>
                </Box>

                <TextField
                  size="small"
                  variant="standard"
                  value={employee.total_hours ?? ""}
                  onChange={(event) =>
                    handleHoursChange(employee.employee_fk, event)
                  }
                  disabled={!employee.editing || employee.updating}
                  placeholder="0"
                  inputProps={{
                    inputMode: "decimal",
                  }}
                  sx={{
                    width: 90,
                    flexShrink: 0,
                    "& .MuiInputBase-root": {
                      height: 32,
                    },
                  }}
                />

                {allowEdit && (
                <Tooltip
                  title={
                    employee.updating
                      ? "Updating..."
                      : employee.editing
                        ? "Save"
                        : "Edit"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={employee?.updating}
                      onClick={() =>
                        employee.editing
                          ? handleSaveRow(employee.employee_fk)
                          : handleEdit(employee.employee_fk)
                      }
                    >
                      {employee.updating ? (
                        <CircularProgress size={18} />
                      ) : employee.editing ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <EditIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>)}
              </Box>
            </Grid>
          ))}

          {employees?.length === 0 && (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={2}
              >
                No employee records found.
              </Typography>
            </Grid>
          )}
        </Grid>
        )}
      </DialogContent>
      {allowEdit && (
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box
          item
          xs={12}
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}
        >
          <Button variant="outlined" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={
              hasUnsavedEdits
                ? "Please save the changes for the employee before continuing."
                : ""
            }
            arrow
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                onClick={handleGenerate}
                disabled={hasUnsavedEdits}
                sx={{
                  ":hover": {
                    color: grey[50],
                    background: red[700],
                  },
                  color: grey[50],
                  background: red[500],
                }}
              >
                Yes
              </Button>
            </span>
          </Tooltip>
        </Box>
      </DialogActions>)}
    </Dialog>
  );
};

export default DailyWageEmployeeWorkingHoursDialog;
